import { Badge, Button, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import Link from '@/components/Link';
import { useHasAnyPermission } from '@/models/permissions';
import {
  type CasePerson,
  type MergeCase,
  PERSON_STATE_COLORS,
  PERSON_STATE_LABELS,
  useDecideCase,
  usePersonCases,
} from '@/models/persons';

const REASONS: Record<string, string> = {
  email_taken: 'Same email',
  phone_match: 'Same phone',
  bo_phone_pair: 'Same phone',
  bo_email_names: 'Same email',
  bo_email_multi: 'Same email',
  contacts_same_key: 'Same email or phone',
  shared_key_names_differ: 'Same email or phone, different name',
  bo_link_conflict: 'Same Monday contact',
  link_only: 'Linked in Monday',
  marketing_person_conflict: 'Different marketing records',
};

const nameOf = (p: CasePerson) =>
  [p.firstName, p.lastName].filter(Boolean).join(' ') || p.email || `#${p.id}`;

function Other({ p }: { p: CasePerson }) {
  return (
    <Stack gap={2}>
      <Group gap={6}>
        <Link to={`~/contacts/${p.id}`}>
          <Text fw={500} fz="sm">
            {nameOf(p)}
          </Text>
        </Link>
        {p.state && (
          <Badge size="xs" color={PERSON_STATE_COLORS[p.state]}>
            {PERSON_STATE_LABELS[p.state]}
          </Badge>
        )}
      </Group>
      <Text fz="xs" c="dimmed">
        {[
          p.email,
          p.phone,
          `${p.requests} requests`,
          `${p.offers} offers`,
          p.members.length ? p.members.join(', ') : 'no members',
          p.mondayItemId ? `Monday ${p.mondayItemId}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Text>
    </Stack>
  );
}

type DupGroup = { key: string; cases: MergeCase[]; others: CasePerson[] };

function groupCases(rows: MergeCase[], contactId: number): DupGroup[] {
  const groups = new Map<string, DupGroup>();
  for (const c of rows) {
    const others = c.people.filter((p) => p.id !== contactId);
    const key = others.length
      ? others
          .map((p) => p.id)
          .sort((a, b) => a - b)
          .join(',')
      : `case:${c.id}`;
    const g = groups.get(key) ?? { key, cases: [], others };
    g.cases.push(c);
    groups.set(key, g);
  }
  return [...groups.values()];
}

function GroupRow({ g, contactId }: { g: DupGroup; contactId: number }) {
  const decide = useDecideCase();
  const reasons = [
    ...new Set(
      g.cases.map((c) => REASONS[c.code] ?? c.code.replaceAll('_', ' ')),
    ),
  ];
  const run = async (decision: 'same' | 'different') => {
    let blocked = false;
    for (const c of g.cases) {
      const r = await decide
        .mutateAsync({
          caseId: c.id,
          decision,
          keepId: decision === 'same' ? contactId : null,
        })
        .catch(() => null);
      if (r?.status === 'blocked') {
        blocked = true;
        break;
      }
    }
    notifications.show({
      color: blocked ? 'orange' : 'green',
      message: blocked
        ? 'Not merged: their offers lead to different marketing records'
        : decision === 'same'
          ? g.others.length
            ? 'Merged into this contact'
            : 'Marked as resolved'
          : 'Marked as not a duplicate',
    });
  };
  return (
    <Stack
      gap={6}
      pb="xs"
      style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
    >
      <Text fz="xs" c="dimmed">
        {reasons.join(' · ')}
      </Text>
      {g.others.length ? (
        g.others.map((p) => <Other key={p.id} p={p} />)
      ) : (
        <Text fz="sm">{g.cases[0]?.comment ?? 'Needs a check'}</Text>
      )}
      <Group gap="xs">
        <Button
          size="xs"
          loading={decide.isPending}
          onClick={() => run('same')}
        >
          {g.others.length ? 'Merge into this contact' : 'Resolved'}
        </Button>
        {g.others.length > 0 && (
          <Button
            size="xs"
            variant="light"
            loading={decide.isPending}
            onClick={() => run('different')}
          >
            Not a duplicate
          </Button>
        )}
      </Group>
    </Stack>
  );
}

export function DuplicatesCard({ contactId }: { contactId: number }) {
  const enabled = useHasAnyPermission(['contacts:assign', 'contacts:*', '*:*']);
  const { data } = usePersonCases(contactId, enabled);
  const groups = groupCases(data?.rows ?? [], contactId);
  if (!enabled || groups.length === 0) return null;
  return (
    <CollapsibleCard title={`Possible duplicates (${groups.length})`}>
      <Stack gap="sm">
        {groups.map((g) => (
          <GroupRow key={g.key} g={g} contactId={contactId} />
        ))}
      </Stack>
    </CollapsibleCard>
  );
}
